/**
 * DEFUSE S15 — typed property descriptors (public entry).
 *
 * The single typed source both the panel-read (S16) and the edit-write (S15
 * cutover) consume, replacing today's ad-hoc string-label routing through
 * `propertyTranslator` (apps/web) + `propertyCapabilities` / `simpleGeometry`
 * `read*State`. Per ПРИОРИТЕТ #1 this is the package-level CAD-core typed model
 * new property semantics land in FIRST; preview / renderer / UI project from it.
 *
 * Cat-B foundation — NOTHING consumes it yet (zero behaviour change). S15 wires
 * the write path onto `descriptor.typedFieldRef` + `constraints`; S16 wires the
 * panel/grid off `getTypedPropertyDescriptors(kind)`.
 *
 * STRUCTURE (S0.5 SLA): this core stays ≤150 LOC — the type model + public
 * resolver. The registry ASSEMBLY lives in `descriptors/index.ts`; per-kind
 * tables + the type model live under `descriptors/` and are re-exported here so
 * the whole model is reachable from this one public module. The type model is a
 * leaf (`descriptors/types.ts`) to break the core→barrel→per-kind→core import
 * cycle (dep-graph zero-cycle gate) — the same cycle-break `geometryEntityTypes`
 * performed for `simpleGeometry`.
 */

import type { EntityType } from "@dxf-ai-editor/contracts";
import type { DrawingEntity } from "@dxf-ai-editor/drawing-model";

import { formatNumber } from "./geometryEntityTypes";
import { TYPED_PROPERTY_DESCRIPTOR_REGISTRY } from "./descriptors";
import type { TypedFieldRef, TypedPropertyDescriptor } from "./descriptors/types";

// ── Public re-exports: the whole typed model surface ──
// The one-liner descriptor factories (num/coord/txt/…) stay internal to
// `descriptors/*`; S15/S16 consume the RESOLVED descriptors + the resolver, not
// the authoring sugar (kept off the generic barrel to avoid `export *` name
// collisions). `propertyMessageKey` + `makePropertyDescriptor` are the public
// authoring surface for a future descriptor.
export {
  propertyMessageKey,
  make as makePropertyDescriptor,
  resolveReadOnly,
} from "./descriptors/types";
export type {
  EditorKind,
  PropertyChannelClass,
  NumericConstraint,
  DescriptorConstraints,
  TypedFieldRef,
  TypedPropertyDescriptor,
} from "./descriptors/types";
export {
  SIDE_CHANNEL_REGISTRY,
  DOCUMENTED_TYPED_SIDE_CHANNELS,
  CLASSIFIED_SIDE_CHANNEL_KEYS,
  type SideChannelKind,
  type SideChannelDescriptor,
  type DocumentedTypedSideChannel,
} from "./descriptors/sideChannels";
export {
  SINGLE_SELECT_BAG_RESIDUAL,
  SINGLE_SELECT_COMMON_BAG_RESIDUAL,
  isSingleSelectCommonBagResidual,
  isSingleSelectCompactNumericResidual,
  isSingleSelectConditionalBagResidual,
  singleSelectBagResidual,
} from "./descriptors/singleSelectResidual";
export { TYPED_PROPERTY_DESCRIPTOR_REGISTRY } from "./descriptors";

/** Every typed property descriptor for an entity kind (common object props ++
 *  the kind's geometry / type-specific fields). Empty-safe for every kind. */
export function getTypedPropertyDescriptors(
  kind: EntityType,
): readonly TypedPropertyDescriptor[] {
  return TYPED_PROPERTY_DESCRIPTOR_REGISTRY[kind] ?? [];
}

/** Look up ONE descriptor by its canonical label (the string-bag key). */
export function getTypedPropertyDescriptor(
  kind: EntityType,
  label: string,
): TypedPropertyDescriptor | undefined {
  return getTypedPropertyDescriptors(kind).find((d) => d.label === label);
}

/**
 * fix/properties-apply-derived-fields-skip — true when (kind, label) is a
 * KNOWN derived/computed display field: shown on the entity, but with no
 * defined way to persist a value back through THIS write path — the same
 * "AutoCAD renders it read-only, never drops it, never a genuine
 * incompatibility" shape, however the typed descriptor happens to be
 * authored. Three recognised shapes, in order:
 *
 *  1. `channel === "read-only-calc"` — the `calc()`-authored descriptors
 *     (Line Length/Angle, Circle Diameter/Circumference/Area, Arc Total
 *     angle/Arc length, LWPOLYLINE Length/Area, Dimension Measurement, …).
 *
 *  2. an EMPTY (or absent) `typedFieldRef.path` with NO `toRaw` transform —
 *     a value that spans multiple fields (or none) with no single field to
 *     write into, AND no defined raw-value transform to route a write
 *     through a side effect either. @codex P2 (PR #591) — this catches
 *     descriptors authored under the default `editable-typed-field` channel
 *     that are nonetheless not yet write-back-wired: Dimension "Third point
 *     X/Y" / "Arc point X/Y" (`descriptors/annotation.ts` `optionalPoint`),
 *     Multileader "Arrow X/Y" / "Landing X/Y" (`derivedCoord`), Revision
 *     Cloud "Corner 1/2" (`derivedCorner`) — each documented "the write
 *     reconciler is an S15 prerequisite". An empty-path descriptor that DOES
 *     have a `toRaw` (Block "Hyperlink", Viewport "Scale" — both explicitly
 *     documented WRITABLE, routed through a `sideEffects` mutator instead of
 *     a plain field set) is correctly EXCLUDED by requiring `!toRaw` too.
 *
 *  3. a STATIC (entity-independent) `constraints.readOnly === true` on an
 *     `editable-typed-field`-channel descriptor — Dimension "Anonymous
 *     block" (a real typed field, but never user-editable). Gated to
 *     `editable-typed-field` specifically so this does NOT sweep in
 *     `ui-projection-deferred` fields (Plot Style / Material / Viewport
 *     Snap/Grid/UCS icon via `deferred()`) — those are a DIFFERENT
 *     "no typed home for the display value" shape, not "computed from
 *     sibling fields", and are not part of the live UI surface this
 *     classifier's callers reach today. A FUNCTION-valued `readOnly` is
 *     entity-instance-dependent and is intentionally left alone — this
 *     predicate has no entity to evaluate it against.
 *
 * The ONE place a shell property-apply gate (`isSimpleGeometryPatchableProperty`
 * callers in `simpleGeometry.ts`, the `patchSet` gate in
 * `extendedPropertyMutations.ts`) consults to classify a rejected change as
 * "derived" rather than "incompatible", so the audit summary
 * (`summarizePropertyMutation`, apps/web `reducerHelpers.ts`) never counts a
 * row that rode along unedited in a full Properties-panel draft submit as a
 * failed write. ПРИОРИТЕТ #1 — the classification lives in the typed
 * CAD-core registry (the one place that already knows a field has no
 * independent typed home), not re-derived ad hoc at each call site.
 */
export function isDerivedDisplayProperty(kind: EntityType, label: string): boolean {
  const descriptor = getTypedPropertyDescriptor(kind, label);
  if (!descriptor) return false;
  if (descriptor.channel === "read-only-calc") return true;
  const path = descriptor.typedFieldRef?.path;
  if ((path === undefined || path === "") && !descriptor.toRaw) return true;
  if (descriptor.channel === "editable-typed-field" && descriptor.constraints?.readOnly === true) {
    return true;
  }
  return false;
}

/** Walk a dotted `typedFieldRef.path` (e.g. "start.x") on a value. Returns
 *  `undefined` for an array-map path (contains "[]") — those resolve only via
 *  the executable `read` accessor. */
export function readTypedFieldPath(entity: DrawingEntity, path: string): unknown {
  if (path === "" || path.includes("[]")) return undefined;
  let cursor: unknown = entity;
  for (const segment of path.split(".")) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = (cursor as Record<string, unknown>)[segment];
  }
  return cursor;
}

/** Resolve a descriptor's typed value against a parsed `DrawingEntity` via its
 *  executable accessor (the canonical resolution S15/S16 use). */
export function resolveTypedFieldValue(
  ref: TypedFieldRef,
  entity: DrawingEntity,
): unknown {
  return ref.read(entity);
}

/** Default display projection applied when a descriptor carries no explicit
 *  `display` — mirrors the production builder: `formatNumber` for ANY numeric
 *  (incl. read-only-calc Length/Area/Arc length), Yes/No for booleans, verbatim
 *  string otherwise. Type-based (not editorKind-based) so a numeric calc value
 *  is rounded exactly like `formatNumber(length)` in dxfGeometry. */
function defaultFormat(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "number") return formatNumber(raw);
  if (typeof raw === "boolean") return raw ? "Yes" : "No";
  return String(raw);
}

/**
 * The panel display STRING for a descriptor against a parsed `DrawingEntity` —
 * the single value S16 renders. Uses the descriptor's explicit `display`
 * transform when present (degree-projected angle / decoded label / serialized
 * array / derived scale), else the default projection for its editorKind. This
 * is what makes S16 a pure projection of the descriptor set: it reproduces
 * `dxfGeometry.buildXxxPreview` output WITHOUT the string bag.
 */
export function resolveDisplay(
  descriptor: TypedPropertyDescriptor,
  entity: DrawingEntity,
): string {
  if (descriptor.display) return descriptor.display(entity);
  if (!descriptor.typedFieldRef) return "";
  return defaultFormat(descriptor.typedFieldRef.read(entity));
}
